import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Phone } from 'lucide-react';
import Logo from "../Assets/logo.png";

const DropdownMenu = ({ items, isOpen, closeMenu }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ opacity: 0, y: -8, scaleY: 0.95 }}
        animate={{ opacity: 1, y: 0, scaleY: 1 }}
        exit={{ opacity: 0, y: -8, scaleY: 0.95 }}
        transition={{ duration: 0.2 }}
        className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50 overflow-hidden"
        style={{ transformOrigin: 'top' }}
      >
        {items.map((item) => (
          <Link
            key={item.label}
            to={item.to}
            onClick={closeMenu}
            className="flex items-center gap-3 px-5 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-800 transition-all duration-200"
          >
            {item.icon && <item.icon className="w-4 h-4 text-blue-600" />}
            <span>{item.label}</span>
          </Link>
        ))}
      </motion.div>
    )}
  </AnimatePresence>
);

const MobileDropdown = ({ items, closeMenu }) => (
  <div className="pl-4 space-y-1 mt-1 mb-2">
    {items.map((item) => (
      <Link
        key={item.label}
        to={item.to}
        onClick={closeMenu}
        className="block px-4 py-2.5 text-gray-600 hover:bg-blue-50 hover:text-blue-800 rounded-lg text-sm transition-all duration-200"
      >
        {item.label}
      </Link>
    ))}
  </div>
);

const NavItem = ({ to, children, end, isScrolled }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) =>
      `relative px-3 py-2 text-sm font-medium transition-colors duration-200 ${
        isActive
          ? 'text-yellow-400'
          : isScrolled
            ? 'text-gray-700 hover:text-blue-800'
            : 'text-white/80 hover:text-white'
      }`
    }
  >
    {({ isActive }) => (
      <>
        {children}
        {isActive && (
          <motion.div
            layoutId="navIndicator"
            className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-5 h-0.5 bg-yellow-400 rounded-full"
          />
        )}
      </>
    )}
  </NavLink>
);

export const Header = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpenDropdown, setMobileOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const dropdownTimeout = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDropdownEnter = (dropdown) => {
    clearTimeout(dropdownTimeout.current);
    setActiveDropdown(dropdown);
  };

  const handleDropdownLeave = () => {
    dropdownTimeout.current = setTimeout(() => setActiveDropdown(null), 200);
  };

  const closeMenus = () => {
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
    setMobileOpenDropdown(null);
  };

  const toggleMobileDropdown = (name) => {
    setMobileOpenDropdown(mobileOpenDropdown === name ? null : name);
  };

  const aboutItems = [
    { label: 'About the Company', to: '/about_build_fast' },
    { label: 'Our Team', to: '/team' },
    { label: 'Careers', to: '/careers' },
  ];

  const serviceItems = [
    { label: 'All Services', to: '/services/services' },
    { label: 'Architecture', to: '/services/architecture' },
    { label: 'Project Management', to: '/services/project-management' },
    { label: 'Quantity Surveying', to: '/services/quantity-surveying' },
    { label: 'Engineering', to: '/services/engineering' },
    { label: 'Town Planning', to: '/services/town-planning' },
    { label: 'Construction', to: '/services/construction' },
  ];

  return (
    <nav
      ref={dropdownRef}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white shadow-lg border-b border-gray-100'
          : 'bg-blue-900/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-screen-xl mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0">
            <img src={Logo} alt="Build-fast" className="h-9 md:h-10" />
            <span className={`text-xl md:text-2xl font-extrabold transition-colors duration-300 ${isScrolled ? 'text-blue-800' : 'text-white'}`}>
              Build<span className="text-yellow-400">Fast</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            <NavItem to="/" end isScrolled={isScrolled}>Home</NavItem>

            <div
              className="relative"
              onMouseEnter={() => handleDropdownEnter('about')}
              onMouseLeave={handleDropdownLeave}
            >
              <button className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors duration-200 rounded-lg ${activeDropdown === 'about' ? 'text-yellow-400' : isScrolled ? 'text-gray-700 hover:text-blue-800' : 'text-white/80 hover:text-white'}`}>
                About <ChevronDown className={`w-3.5 h-3.5 transition-transform ${activeDropdown === 'about' ? 'rotate-180' : ''}`} />
              </button>
              <DropdownMenu items={aboutItems} isOpen={activeDropdown === 'about'} closeMenu={closeMenus} />
            </div>

            <div
              className="relative"
              onMouseEnter={() => handleDropdownEnter('services')}
              onMouseLeave={handleDropdownLeave}
            >
              <button className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors duration-200 rounded-lg ${activeDropdown === 'services' ? 'text-yellow-400' : isScrolled ? 'text-gray-700 hover:text-blue-800' : 'text-white/80 hover:text-white'}`}>
                Services <ChevronDown className={`w-3.5 h-3.5 transition-transform ${activeDropdown === 'services' ? 'rotate-180' : ''}`} />
              </button>
              <DropdownMenu items={serviceItems} isOpen={activeDropdown === 'services'} closeMenu={closeMenus} />
            </div>

            <NavItem to="/projects" isScrolled={isScrolled}>Projects</NavItem>
            <NavItem to="/gallery" isScrolled={isScrolled}>Gallery</NavItem>
            <NavItem to="/contact-us" isScrolled={isScrolled}>Contact</NavItem>
          </div>

          {/* Desktop Right Side */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:+263776347735"
              className={`flex items-center gap-2 text-sm font-medium transition-colors duration-200 ${isScrolled ? 'text-gray-600 hover:text-blue-800' : 'text-white/70 hover:text-white'}`}
            >
              <Phone className="w-4 h-4" />
              +263 776 347 735
            </a>
            <Link
              to="/quotation"
              className="bg-yellow-400 hover:bg-yellow-300 text-blue-900 font-bold py-2.5 px-6 rounded-xl text-sm transition-all duration-300 shadow-lg shadow-yellow-400/20 hover:shadow-yellow-400/30"
            >
              Get a Quote
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${isScrolled ? 'text-blue-800 hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-t border-gray-100 shadow-xl overflow-hidden"
          >
            <div className="px-4 py-4 space-y-1 max-h-[70vh] overflow-y-auto">
              <NavLink to="/" end onClick={closeMenus} className={({ isActive }) => `block px-4 py-3 rounded-xl text-base font-medium transition-all ${isActive ? 'bg-blue-50 text-blue-800' : 'text-gray-700 hover:bg-gray-50'}`}>Home</NavLink>

              <div>
                <button onClick={() => toggleMobileDropdown('about')} className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:bg-gray-50 transition-all">
                  About <ChevronDown className={`w-4 h-4 transition-transform ${mobileOpenDropdown === 'about' ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {mobileOpenDropdown === 'about' && (
                    <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                      <MobileDropdown items={aboutItems} closeMenu={closeMenus} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div>
                <button onClick={() => toggleMobileDropdown('services')} className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-base font-medium text-gray-700 hover:bg-gray-50 transition-all">
                  Services <ChevronDown className={`w-4 h-4 transition-transform ${mobileOpenDropdown === 'services' ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {mobileOpenDropdown === 'services' && (
                    <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                      <MobileDropdown items={serviceItems} closeMenu={closeMenus} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <NavLink to="/projects" onClick={closeMenus} className={({ isActive }) => `block px-4 py-3 rounded-xl text-base font-medium transition-all ${isActive ? 'bg-blue-50 text-blue-800' : 'text-gray-700 hover:bg-gray-50'}`}>Projects</NavLink>
              <NavLink to="/gallery" onClick={closeMenus} className={({ isActive }) => `block px-4 py-3 rounded-xl text-base font-medium transition-all ${isActive ? 'bg-blue-50 text-blue-800' : 'text-gray-700 hover:bg-gray-50'}`}>Gallery</NavLink>
              <NavLink to="/contact-us" onClick={closeMenus} className={({ isActive }) => `block px-4 py-3 rounded-xl text-base font-medium transition-all ${isActive ? 'bg-blue-50 text-blue-800' : 'text-gray-700 hover:bg-gray-50'}`}>Contact</NavLink>

              <div className="pt-4 space-y-3 border-t border-gray-100 mt-4">
                <a href="tel:+263776347735" className="flex items-center gap-3 px-4 py-3 text-gray-600 hover:text-blue-800 transition-colors"><Phone className="w-4 h-4" /> +263 776 347 735</a>
                <Link to="/quotation" onClick={closeMenus} className="block w-full text-center bg-yellow-400 hover:bg-yellow-300 text-blue-900 font-bold py-3.5 rounded-xl transition-all duration-300 shadow-lg">
                  Get a Quote
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Header;
